import React, { useMemo, useState } from 'react';
import '../../styles/data.css';
import { useAuth } from '../../hooks/useAuth';
import { canAccessDataTab } from '../../utils/rolePermissions';
import BatchesSection from '../data/BatchesSection';
import CoursesSection from '../data/CoursesSection';
import ChaptersSection from '../data/ChaptersSection';
import ExamsSection from '../data/ExamsSection';
import MentorsSection from '../data/MentorsSection';
import ExamAttemptsSyncSection from '../data/ExamAttemptsSyncSection';
import CalendarSection from '../data/CalendarSection';
import MonthPlannerSection from '../data/MonthPlannerSection';
import HierarchySection from '../data/hierarchy/HierarchySection';

const TABS = [
  { key: 'batches',       label: 'Batches',       icon: 'bi-people-fill' },
  { key: 'mentors',       label: 'Mentors',       icon: 'bi-person-workspace' },
  { key: 'hierarchy',     label: 'Hierarchy',     icon: 'bi-diagram-3' },
  { key: 'courses',       label: 'Courses',       icon: 'bi-book-fill' },
  { key: 'chapters',      label: 'Chapters',      icon: 'bi-list-ul' },
  { key: 'exams',         label: 'Exams',         icon: 'bi-clipboard-check-fill' },
  { key: 'calendar',      label: 'Calendar',      icon: 'bi-calendar3' },
  { key: 'month-planner', label: 'Month Planner', icon: 'bi-calendar-range' },
  { key: 'exam-attempts', label: 'Exam Attempts', icon: 'bi-arrow-repeat' },
];

// Schedule Builder was replaced by Month Planner. The tab is gone from the sub-nav,
// but the key is still handled so an old bookmark or link explains what happened
// instead of rendering nothing.
const RetiredScheduleBuilderNotice = () => (
  <div className="data-list-empty">
    <i className="bi bi-calendar-x data-list-empty-icon" />
    <p className="data-list-empty-msg">
      <strong>Schedule Builder has been retired.</strong>
      <br />
      Use <strong>Month Planner</strong> to create class schedules for a group of batches.
    </p>
  </div>
);

const DataPage = () => {
  const { role } = useAuth();

  // Roles see different slices of this sub-nav; TABS keeps the on-screen order.
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => canAccessDataTab(role, tab.key)),
    [role]
  );

  const [requestedTab, setRequestedTab] = useState(null);

  // Land on the first tab this role may open rather than a hardcoded one, and
  // fall back to it if the active tab is not one of theirs.
  const activeTab = visibleTabs.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : visibleTabs[0]?.key ?? null;

  const renderSection = () => {
    if (!canAccessDataTab(role, activeTab)) return null;

    switch (activeTab) {
      case 'batches':  return <BatchesSection />;
      case 'mentors':  return <MentorsSection />;
      case 'hierarchy': return <HierarchySection />;
      case 'courses':  return <CoursesSection />;
      case 'chapters': return <ChaptersSection />;
      case 'exams':    return <ExamsSection />;
      case 'calendar': return <CalendarSection />;
      case 'schedule-builder': return <RetiredScheduleBuilderNotice />;
      case 'month-planner': return <MonthPlannerSection />;
      case 'exam-attempts': return <ExamAttemptsSyncSection />;
      default:         return null;
    }
  };

  return (
    <div className="data-page">
      {/* Page header */}
      <div>
        <h2 className="data-page-title">Data</h2>
        <p className="data-page-subtitle">
          Browse synced batches, mentors, courses, chapters, and exams, or sync a single exam&rsquo;s attempts.
        </p>
      </div>

      {/* Sub-navigation pills */}
      <nav className="data-subnav" aria-label="Data sections">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            className={`data-subnav-pill${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setRequestedTab(tab.key)}
            aria-current={activeTab === tab.key ? 'page' : undefined}
          >
            <i className={`bi ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Active section */}
      <div className="data-section">
        {renderSection()}
      </div>
    </div>
  );
};

export default DataPage;
